import { LiftType } from "../flatbuffers/generated/archive/lift-type";

/**
 * Numeric device-type constants matching the server's Messages API `device_type` field.
 * These occupy bits 21-20 of the composite debounce key (2 bits, values 0-3).
 */

export const DEVICE_ELEVATOR = 0;
export const DEVICE_ESCALATOR = 1;
export const DEVICE_STATUS = 2;

/**
 * Pack device type, MessageContent id, and enum code into a single 32-bit integer.
 *
 * Layout (22 bits used):
 *   bits 21-20 : device type   (2 bits, 0-3)
 *   bits 19-16 : message content id (4 bits, 0-15)
 *   bits 15-0  : enum code     (16 bits, 0-65535)
 */
export function debounceKey(deviceType: number, messageContent: number, code: number): number {
  return (deviceType % 4) * 1048576 + (messageContent % 16) * 65536 + (code % 65536);
}

/**
 * Map the server's `device_type` string to a numeric constant for composite keys.
 */
export function deviceTypeFromString(deviceType: string): number {
  switch (deviceType) {
    case LiftType[LiftType.ELEVATOR]:
      return DEVICE_ELEVATOR;
    case LiftType[LiftType.ESCALATOR]:
      return DEVICE_ESCALATOR;
    case "STATUS":
      return DEVICE_STATUS;
    default:
      return DEVICE_ELEVATOR;
  }
}

/**
 * Map the FlatBuffer-generated `LiftType` numeric enum to a composite-key device type.
 * Use this when the source is a FlatBuffer T-class (e.g., DebounceOverrideT.fbsDeviceType,
 * KeyLightT.fbsDeviceType) — those fields are numeric enum values, not strings.
 */
export function deviceTypeFromLiftType(deviceType: LiftType): number {
  switch (deviceType) {
    case LiftType.ELEVATOR:
      return DEVICE_ELEVATOR;
    case LiftType.ESCALATOR:
      return DEVICE_ESCALATOR;
    default:
      return DEVICE_ELEVATOR;
  }
}

/**
 * Resolves the debounce time for a given bank/lift/message using 3-tier lookup:
 *   1. Lift-specific override  (bankId:liftId:compositeKey)
 *   2. Bank-wide override      (bankId:*:compositeKey)
 *   3. Adapter key default     (compositeKey as number)
 *
 * The composite key encodes device type, message content, and enum code
 * into a single number, preventing collisions between different message types
 * that share the same enum_id.
 *
 * Returns 0 when nothing is configured (display immediately).
 */
export default function resolveDebounceTime(
  overrideMap: Map<string, number>,
  defaultMap: Map<number, number>,
  bankId: string,
  liftId: string,
  compositeKey: number,
): number {
  return (
    overrideMap.get(`${bankId}:${liftId}:${compositeKey}`) ??
    overrideMap.get(`${bankId}:*:${compositeKey}`) ??
    defaultMap.get(compositeKey) ??
    0
  );
}
